import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { IReviewRequest, IReviewResponse } from "interfaces/IReviews";
import { postReview, createAuthenticatedAxiosInstance, getMyReview } from "api/auth";
import "./ReviewModal.scss";
import { useAuthStore } from "../../../../../zustand/loginStore";
import { useBreakpoints } from "hooks/useBreakPoints";

interface ReviewModalProps {
  orderProducts: Array<{
    product_id: number;
    product_name: string;
    images: string[];
  }>;
  userId: number;
  onClose: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ orderProducts, userId, onClose }) => {
  const [reviews, setReviews] = useState<{
    [productId: number]: {
      qualification: number;
      comment: string;
      submitted: boolean;
      loading: boolean;
    };
  }>({});
  const [submitting, setSubmitting] = useState<{ [productId: number]: boolean }>({});
  const { token } = useAuthStore();
  const { isMobile } = useBreakpoints();
  const axiosInstance = createAuthenticatedAxiosInstance(token);

  // Al abrir el modal, consultar si ya existe review para cada producto
  useEffect(() => {
    orderProducts.forEach(async (prod) => {
      setReviews((prev) => ({
        ...prev,
        [prod.product_id]: { ...prev[prod.product_id], loading: true },
      }));
      try {
        const review = await getMyReview(axiosInstance, prod.product_id);
        if (review) {
          setReviews((prev) => ({
            ...prev,
            [prod.product_id]: {
              qualification: review.qualification,
              comment: review.comment,
              submitted: true,
              loading: false,
            },
          }));
        } else {
          setReviews((prev) => ({
            ...prev,
            [prod.product_id]: {
              qualification: 0,
              comment: "",
              submitted: false,
              loading: false,
            },
          }));
        }
      } catch {
        setReviews((prev) => ({
          ...prev,
          [prod.product_id]: {
            qualification: 0,
            comment: "",
            submitted: false,
            loading: false,
          },
        }));
      }
    });
    // eslint-disable-next-line
  }, [orderProducts, userId, token]);

  const handleStarClick = (productId: number, star: number) => {
    if (reviews[productId]?.submitted) return;
    setReviews((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        qualification: star,
      },
    }));
  };

  const handleCommentChange = (productId: number, comment: string) => {
    if (reviews[productId]?.submitted) return;
    setReviews((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        comment,
      },
    }));
  };

  const handleSubmit = async (productId: number) => {
    if (!reviews[productId]?.qualification || reviews[productId]?.submitted) return;
    setSubmitting((prev) => ({ ...prev, [productId]: true }));
    try {
      const review: IReviewRequest = {
        product_id: productId,
        user_id: userId,
        comment: reviews[productId]?.comment || "",
        qualification: reviews[productId]?.qualification,
      };
      await postReview(axiosInstance, review);
      setReviews((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          submitted: true,
        },
      }));
    } catch (e) {
      // Manejar error
    }
    setSubmitting((prev) => ({ ...prev, [productId]: false }));
  };

  const modalContent = (
    <div className="review-modal-overlay">
      <div className={`review-modal-content ${isMobile ? 'mobile' : ''}`}>
        <div className="review-modal-header">
          <h3>Califica tu pedido</h3>
          <span
            className="review-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </span>
        </div>
        <div className="review-modal-products">
          {orderProducts.map((prod) => (
            <div key={prod.product_id} className={`review-modal-product ${isMobile ? 'mobile' : ''}`}>
              {isMobile ? (
                // Layout móvil: imagen arriba, texto centrado, luego todo hacia la izquierda
                <div className="mobile-review-layout">
                  <div className="mobile-review-image-container">
                    <img
                      src={prod.images[0]}
                      alt={prod.product_name}
                      className="mobile-review-product-image"
                    />
                  </div>
                  <div className="mobile-review-product-title">
                    {prod.product_name}
                  </div>
                  <div className="mobile-review-rating-section">
                    <div className="mobile-review-stars-label">
                      Calificación:
                    </div>
                    <div className="mobile-review-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`mobile-review-star${
                            (reviews[prod.product_id]?.qualification || 0) >= star
                              ? " active"
                              : ""
                          }`}
                          onClick={() => handleStarClick(prod.product_id, star)}
                          role="button"
                          aria-label={`Calificar con ${star} estrellas`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mobile-review-comment-section">
                    <div className="mobile-review-comment-label">
                      Comentarios:
                    </div>
                    <textarea
                      className="mobile-review-comment"
                      value={reviews[prod.product_id]?.comment || ""}
                      onChange={(e) =>
                        handleCommentChange(prod.product_id, e.target.value)
                      }
                      placeholder="Añade tu comentario"
                      disabled={!!reviews[prod.product_id]?.submitted}
                    />
                    <div className="mobile-review-btn-container">
                      <button
                        className="mobile-review-submit-btn"
                        style={{
                          background: reviews[prod.product_id]?.qualification
                            ? "#997404"
                            : "#747775",
                          cursor: reviews[prod.product_id]?.qualification && !reviews[prod.product_id]?.submitted
                            ? "pointer"
                            : "not-allowed",
                          opacity: submitting[prod.product_id] ? 0.7 : 1,
                        }}
                        disabled={
                          !reviews[prod.product_id]?.qualification ||
                          submitting[prod.product_id] ||
                          !!reviews[prod.product_id]?.submitted
                        }
                        onClick={() => handleSubmit(prod.product_id)}
                      >
                        {reviews[prod.product_id]?.submitted
                          ? "¡Calificado!"
                          : "Añadir calificación"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Layout desktop original
                <>
                  <div className="review-modal-stars-row">
                    <img
                      src={prod.images[0]}
                      alt={prod.product_name}
                      className="review-modal-product-image"
                    />
                    <div className="review-modal-product-title">
                      {prod.product_name}
                    </div>
                  </div>
                  <div className="review-modal-product-info">
                    <div className="review-modal-stars-row">
                      <span className="review-modal-stars-label">
                        Calificación:
                      </span>
                      <div className="review-modal-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`review-modal-star${
                              (reviews[prod.product_id]?.qualification || 0) >= star
                                ? " active"
                                : ""
                            }`}
                            onClick={() => handleStarClick(prod.product_id, star)}
                            role="button"
                            aria-label={`Calificar con ${star} estrellas`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="review-modal-comment-label">
                      Comentario: <span>(Opcional)</span>
                    </div>
                    <textarea
                      className="review-modal-comment"
                      value={reviews[prod.product_id]?.comment || ""}
                      onChange={(e) =>
                        handleCommentChange(prod.product_id, e.target.value)
                      }
                      placeholder="Añade tu comentario"
                      disabled={!!reviews[prod.product_id]?.submitted}
                    />
                    <div className="review-modal-btn-row">
                      <button
                        className="review-modal-submit-btn"
                        style={{
                          background: reviews[prod.product_id]?.qualification
                            ? "#997404"
                            : "#747775",
                          cursor: reviews[prod.product_id]?.qualification && !reviews[prod.product_id]?.submitted
                            ? "pointer"
                            : "not-allowed",
                          opacity: submitting[prod.product_id] ? 0.7 : 1,
                        }}
                        disabled={
                          !reviews[prod.product_id]?.qualification ||
                          submitting[prod.product_id] ||
                          !!reviews[prod.product_id]?.submitted
                        }
                        onClick={() => handleSubmit(prod.product_id)}
                      >
                        {reviews[prod.product_id]?.submitted
                          ? "¡Calificado!"
                          : "Añadir calificación"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ReviewModal;
